import React, { useEffect, useId, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import {
  getDetailCustomFieldValue,
  getFieldValue,
  pickCustomFieldProps,
} from '../../util/fieldHelpers';
import {
  getCurrentUserTypeRoles,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  H2,
  Page,
  ListingCard,
  Reviews,
  LayoutSingleColumn,
  NamedRedirect,
  CustomExtendedDataSection,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import ProfileHeader, { getProfileIdentity } from './ProfileHeader';

import css from './ProfilePage.module.css';

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig, intl } = props;

  const shouldPickUserField = fieldConfig =>
    ['public', 'metadata'].includes(fieldConfig?.scope) &&
    fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      userFieldConfig,
      'userType',
      shouldPickUserField
    ) || [];

  const pickUserFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, userTypeConfig = {}, showConfig = {} } = config;
    const { limitToUserTypeIds, userTypeIds } = userTypeConfig;
    const userType = publicData.userType;
    const isTargetUserType = !limitToUserTypeIds || userTypeIds.includes(userType);

    const { label, displayInProfile } = showConfig;
    const publicDataValue = getFieldValue(publicData, key);
    const metadataValue = getFieldValue(metadata, key);
    const value = publicDataValue !== null ? publicDataValue : metadataValue;

    if (displayInProfile && isTargetUserType && value !== null) {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ProfilePage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };
  const sectionDetailsProps = {
    ...props,
    fieldConfigs: userFieldConfig,
    heading: 'ProfilePage.detailsTitle',
    rootClassName: css.userFieldSection,
  };

  return (
    <CustomExtendedDataSection
      sectionDetailsProps={sectionDetailsProps}
      propsForCustomFields={propsForCustomFields}
      idPrefix="profilePage"
      pickExtendedDataFields={pickUserFields}
      rootClassName={css.userFieldSection}
    />
  );
};

/** Public seller content with one responsive, keyboard-accessible tab navigation. */
export const MainContent = props => {
  const {
    user,
    userShowError,
    bio,
    displayName,
    listings = [],
    queryListingsError,
    queryListingsInProgress = false,
    reviews = [],
    queryReviewsError,
    queryReviewsInProgress = false,
    salesCount = null,
    salesInProgress = false,
    publicData = {},
    metadata = {},
    userFieldConfig = [],
    intl,
    hideReviews,
    userTypeRoles = {},
    showEditLink,
  } = props;
  const [selectedTab, setSelectedTab] = useState('offers');
  const id = useId();
  const tabRefs = useRef([]);
  const activeTab = hideReviews ? 'offers' : selectedTab;
  const tabs = [
    {
      key: 'offers',
      label: intl.formatMessage(
        {
          id:
            queryListingsInProgress || queryListingsError
              ? 'ProfilePage.offersTab'
              : 'ProfilePage.offersTabCount',
        },
        { count: listings.length }
      ),
    },
    ...(!hideReviews
      ? [{ key: 'reviews', label: intl.formatMessage({ id: 'ProfilePage.reviewsTab' }) }]
      : []),
  ];
  const handleTabKeyDown = (event, index) => {
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
        ? tabs.length - 1
        : event.key === 'ArrowRight'
        ? (index + 1) % tabs.length
        : event.key === 'ArrowLeft'
        ? (index + tabs.length - 1) % tabs.length
        : null;
    if (nextIndex !== null) {
      event.preventDefault();
      setSelectedTab(tabs[nextIndex].key);
      tabRefs.current[nextIndex]?.focus();
    }
  };
  const showReviews = () => {
    setSelectedTab('reviews');
    tabRefs.current[1]?.focus();
  };
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const showSellerReviews =
    userTypeRoles.provider || !userTypeRoles.customer || reviewsOfProvider.length > 0;
  const showBuyerReviews =
    reviewsOfCustomer.length > 0 || (!showSellerReviews && userTypeRoles.customer);
  const reviewSections = [
    ...(showSellerReviews
      ? [
          {
            key: 'seller',
            reviews: reviewsOfProvider,
            title: 'ProfilePage.sellerReviews',
            empty: 'ProfilePage.noSellerReviews',
          },
        ]
      : []),
    ...(showBuyerReviews
      ? [
          {
            key: 'buyer',
            reviews: reviewsOfCustomer,
            title: 'ProfilePage.buyerReviews',
            empty: 'ProfilePage.noBuyerReviews',
          },
        ]
      : []),
  ];
  // Identity fields are displayed in the header; preserve other configured public fields.
  const { displayedFieldKeys } = getProfileIdentity(publicData, userFieldConfig, intl);
  const additionalFields = userFieldConfig.filter(field => {
    const value = getFieldValue(publicData, field.key) ?? getFieldValue(metadata, field.key);
    const { limitToUserTypeIds, userTypeIds = [] } = field.userTypeConfig || {};
    return (
      !(field.scope === 'public' && displayedFieldKeys.includes(field.key)) &&
      ['public', 'metadata'].includes(field.scope) &&
      field.showConfig?.displayInProfile !== false &&
      value != null &&
      (!limitToUserTypeIds || userTypeIds.includes(publicData.userType))
    );
  });

  if (userShowError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  return (
    <div className={css.profile}>
      <ProfileHeader
        user={user}
        userFieldConfig={userFieldConfig}
        displayName={displayName}
        bio={bio}
        reviews={reviews}
        salesCount={salesCount}
        salesInProgress={salesInProgress}
        queryReviewsInProgress={queryReviewsInProgress}
        queryReviewsError={queryReviewsError}
        hideReviews={hideReviews}
        showEditLink={showEditLink}
        onShowReviews={showReviews}
      />
      {additionalFields.length ? (
        <details className={css.profileDetails}>
          <summary className={css.detailsSummary}>
            <FormattedMessage id="ProfilePage.profileDetails" />
          </summary>
          <div className={css.detailsContent}>
            <CustomUserFields
              publicData={publicData}
              metadata={metadata}
              userFieldConfig={additionalFields}
              intl={intl}
            />
          </div>
        </details>
      ) : null}
      <div
        className={css.tabs}
        role="tablist"
        aria-label={intl.formatMessage({ id: 'ProfilePage.navigationLabel' })}
      >
        {tabs.map((tab, index) => (
          <button
            key={tab.key}
            ref={element => (tabRefs.current[index] = element)}
            type="button"
            role="tab"
            id={`${id}-${tab.key}-tab`}
            aria-controls={`${id}-${tab.key}-panel`}
            aria-selected={activeTab === tab.key}
            tabIndex={activeTab === tab.key ? 0 : -1}
            className={classNames(css.tab, { [css.tabSelected]: activeTab === tab.key })}
            onClick={() => setSelectedTab(tab.key)}
            onKeyDown={event => handleTabKeyDown(event, index)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <section
        role="tabpanel"
        id={`${id}-offers-panel`}
        aria-labelledby={`${id}-offers-tab`}
        hidden={activeTab !== 'offers'}
        tabIndex={0}
        className={css.tabPanel}
        aria-busy={queryListingsInProgress}
      >
        {queryListingsInProgress ? (
          <p className={css.status} role="status">
            <FormattedMessage id="ProfilePage.offersLoading" />
          </p>
        ) : queryListingsError ? (
          <p className={css.error} role="alert">
            <FormattedMessage id="ProfilePage.loadingDataFailed" />
          </p>
        ) : listings.length ? (
          <>
            <H2 rootClassName={css.sectionTitle}>
              <FormattedMessage id="ProfilePage.offersHeading" values={{ name: displayName }} />
            </H2>
            <ul className={css.listings}>
              {listings.map((listing, index) => (
                <li className={css.listing} key={listing.id.uuid}>
                  <ListingCard
                    className={css.listingCard}
                    listing={listing}
                    showAuthorInfo={false}
                    analyticsSource="profile_page"
                    analyticsPosition={index + 1}
                    renderSizes="(min-width: 1024px) 350px, (min-width: 768px) 45vw, 90vw"
                  />
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div className={css.emptyState}>
            <H2 rootClassName={css.emptyTitle}>
              <FormattedMessage id="ProfilePage.noOffersTitle" />
            </H2>
            <p className={css.emptyText}>
              <FormattedMessage id="ProfilePage.noOffersText" values={{ name: displayName }} />
            </p>
          </div>
        )}
      </section>
      {!hideReviews ? (
        <section
          role="tabpanel"
          id={`${id}-reviews-panel`}
          aria-labelledby={`${id}-reviews-tab`}
          hidden={activeTab !== 'reviews'}
          tabIndex={0}
          className={css.tabPanel}
          aria-busy={queryReviewsInProgress}
        >
          {queryReviewsInProgress ? (
            <p className={css.status} role="status">
              <FormattedMessage id="ProfilePage.reviewsLoading" />
            </p>
          ) : queryReviewsError ? (
            <p className={css.error} role="alert">
              <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
            </p>
          ) : (
            reviewSections.map(section => (
              <div className={css.reviewsSection} key={section.key}>
                <div className={css.reviewHeading}>
                  <H2 rootClassName={css.sectionTitle}>
                    <FormattedMessage id={section.title} />
                  </H2>
                  {section.reviews.length ? (
                    <span className={css.reviewCount}>
                      <FormattedMessage
                        id="ProfilePage.reviewsCount"
                        values={{ count: section.reviews.length }}
                      />
                    </span>
                  ) : null}
                </div>
                {section.reviews.length ? (
                  <Reviews reviews={section.reviews} />
                ) : (
                  <p className={css.emptyText}>
                    <FormattedMessage id={section.empty} />
                  </p>
                )}
              </div>
            ))
          )}
        </section>
      ) : null}
    </div>
  );
};

/**
 * ProfilePageComponent
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.useCurrentUser - Whether to use the current user
 * @param {propTypes.user|propTypes.currentUser} props.user - The user
 * @param {propTypes.error} props.userShowError - The user show error
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Array<propTypes.listing|propTypes.ownListing>} props.listings - The listings
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.queryReviewsError - The query reviews error
 * @returns {JSX.Element} ProfilePageComponent
 */
export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const userTypeRoles = getCurrentUserTypeRoles(config, profileUser);

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }

  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: profileUser?.attributes?.profile?.displayName,
        },
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.main}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <MainContent
          key={pathParams.id}
          user={profileUser}
          showEditLink={mounted && isCurrentUser}
          bio={bio}
          displayName={displayName}
          userShowError={userShowError}
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFields}
          hideReviews={hasNoViewingRightsOnPrivateMarketplace}
          intl={intl}
          userTypeRoles={userTypeRoles}
          {...rest}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    queryListingsInProgress,
    queryReviewsInProgress,
    userListingRefs,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    queryListingsInProgress,
    queryReviewsInProgress,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
    salesCount: state.sellerSales?.counts?.[userId?.uuid] ?? null,
    salesInProgress: state.sellerSales?.inProgress?.[userId?.uuid] || false,
  };
};

const ProfilePage = compose(connect(mapStateToProps))(ProfilePageComponent);

export default ProfilePage;
